import { useRef, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  GlassCard,
} from '@jumpstone/ui-react';
import { format_date } from '../../../lib/format';
import { worker_type_capabilities } from '../../../lib/worker_types';
import { copyTextToClipboard } from '../../../lib/clipboard';
import { useEssInvites } from '../../../data/use_ess_invites';
import type {
  EssActionFailure,
  EssActionResult,
  EssCreateLinkFallback,
  EssCreateResult,
} from '../../../data/use_ess_invites';
import {
  essInviteActionFor,
  isEssInviteExpired,
  type EssInvite,
} from '../../../data/wagepro_store_ess_invites';
import type { Employee } from '../../../data/types';
import {
  ESS_ACTION_ERROR_KEY,
  EssInviteActionLabel,
  EssInviteRowBadge,
  EssStatusBadge,
  type EssInviteRowStatus,
} from '../ess_status_badge';
import { EssInviteLinkDialog } from '../ess_invite_link_dialog';

/**
 * ESS invite management (#23314) — one employee at a time. Reads
 * `payroll.listEssInvites` entity-wide and filters client-side to just the
 * profile's own invites; writes `payroll.createEssInvite`,
 * `payroll.revokeEssInvite`, `payroll.revokeEssAccess` on the profile
 * employee. See `use_ess_invites.ts` for the state machine and failure
 * classification, and `wagepro_store_ess_invites.ts` for the pure UI
 * projection.
 *
 * The server emails the invitation (a set-password link plus a one-time code
 * for the WagePro app) as part of the mint. What the employer sees depends on
 * `email_sent`:
 *
 *   - **emailed** — an inline confirmation naming the address it went to. No
 *     link, no copy button: the server returns no credential once the email is
 *     out, and the one-time code is never returned at all.
 *   - **email failed** — the only case the server returns the raw link. A
 *     **reveal-once** dialog (plus the inline panel it hands off to) shows it
 *     with a "Copy link" affordance and says out loud that the email did not
 *     go, so the employer does not assume the employee was notified.
 *
 * Revokes go through a **destructive-confirm** dialog — never a bare button
 * that would post the mutation immediately.
 */
export function AccessTab({
  employee,
}: {
  employee: Employee;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const ess = useEssInvites();

  const [reveal_open, set_reveal_open] = useState(false);
  const [revealed, set_revealed] = useState<EssCreateLinkFallback | null>(null);
  /** Set after an invite was successfully EMAILED — the address it went to. */
  const [emailed_to, set_emailed_to] = useState<string | null>(null);
  const [copied, set_copied] = useState(false);
  // Set when a copy attempt reached neither clipboard mechanism, so the UI can
  // tell the employer to select the link by hand instead of leaving a dead
  // button. See `lib/clipboard.ts` for why copying can fail outright.
  const [copy_failed, set_copy_failed] = useState(false);
  /**
   * The on-screen element showing the link. Handed to `copyTextToClipboard` so
   * it can copy by selecting real, painted content — the path Android WebView
   * actually honours — instead of only a synthetic off-screen node.
   */
  const link_element_ref = useRef<HTMLDivElement | null>(null);
  const [pending_revoke_invite, set_pending_revoke_invite] = useState<
    string | null
  >(null);
  const [revoke_access_open, set_revoke_access_open] = useState(false);
  const [action_error, set_action_error] = useState<EssActionFailure | null>(
    null,
  );
  const [is_submitting, set_is_submitting] = useState(false);

  if (ess.state.status === 'loading') {
    return (
      <GlassCard className="rounded-2xl p-5">
        <span className="sr-only">{t('employees.ess_loading')}</span>
        <div className="nv-skel" style={{ height: 64, margin: 8 }} />
        <div className="nv-skel" style={{ height: 44, margin: 8 }} />
      </GlassCard>
    );
  }

  if (ess.state.status === 'entitlement_required') {
    return (
      <EmptyState
        title={t('employees.ess_load_entitlement_required_title')}
        description={t('employees.ess_load_entitlement_required_description')}
      />
    );
  }

  if (ess.state.status === 'error' || ess.state.status === 'no_entity') {
    return (
      <EmptyState
        title={t('employees.ess_load_error_title')}
        description={t('employees.ess_load_error_description')}
        action={
          <Button variant="secondary" onClick={ess.retry}>
            {t('employees.profile_retry')}
          </Button>
        }
      />
    );
  }

  const status = ess.status_for_employee(employee.id);
  // No client-side email preflight: the local UI `Employee` shape does not
  // carry an email field (see types.ts §Employee), and the backend's
  // `createEssInvite` already rejects a no-email employee with a
  // BAD_REQUEST classified here as `no_email` — so we let the mutation run
  // and render the classified error in `action_error` instead of guessing
  // the state client-side.
  const now_iso = new Date().toISOString();
  const per_employee_invites = ess.invites.filter(
    (invite) => invite.employee_id === employee.id,
  );

  async function handle_create(): Promise<void> {
    if (!ess.create_invite) return;
    set_action_error(null);
    set_is_submitting(true);
    const result = await ess.create_invite(employee.id);
    set_is_submitting(false);
    on_create_settled(result);
  }

  function on_create_settled(result: EssCreateResult): void {
    if (result.outcome === 'success') {
      set_copied(false);
      set_copy_failed(false);
      if (result.value.email_sent) {
        set_revealed(null);
        set_emailed_to(result.value.invited_email);
        return;
      }
      set_emailed_to(null);
      set_revealed(result.value);
      set_reveal_open(true);
      return;
    }
    set_action_error(result.failure);
  }

  async function handle_revoke_invite(invite_id: string): Promise<void> {
    if (!ess.revoke_invite) return;
    set_action_error(null);
    set_is_submitting(true);
    const result = await ess.revoke_invite(invite_id);
    set_is_submitting(false);
    on_action_settled(result);
    if (result.outcome === 'success') set_pending_revoke_invite(null);
  }

  async function handle_revoke_access(): Promise<void> {
    if (!ess.revoke_access) return;
    set_action_error(null);
    set_is_submitting(true);
    const result = await ess.revoke_access(employee.id);
    set_is_submitting(false);
    on_action_settled(result);
    if (result.outcome === 'success') set_revoke_access_open(false);
  }

  function on_action_settled(result: EssActionResult): void {
    if (result.outcome === 'failure') set_action_error(result.failure);
  }

  /**
   * The ONE place the revealed token leaves component state. Kept as a named
   * function (rather than an inline setter) so the "who can drop the link"
   * question has exactly one answer, which is what Finding #2 was really
   * protecting.
   */
  function dismiss_revealed(): void {
    set_revealed(null);
    set_copied(false);
    set_copy_failed(false);
  }

  async function copy_link_to_clipboard(): Promise<void> {
    if (!revealed) return;
    const ok = await copyTextToClipboard(
      revealed.invite_link,
      link_element_ref.current,
    );
    set_copied(ok);
    set_copy_failed(!ok);
  }

  // Self-service is for employees. A contractor cannot be invited (the API
  // refuses with LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE); an existing link
  // can still be revoked below.
  const ess_allowed = worker_type_capabilities(employee.employment_type).ess;
  const invite_action = essInviteActionFor(status);
  const create_button_disabled =
    !ess.create_invite ||
    is_submitting ||
    invite_action === null ||
    !ess_allowed;

  return (
    <>
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <GlassCard className="rounded-2xl p-5">
          <h2 className="text-[14.5px] font-bold mb-1.5">
            {t('employees.ess_tab_title')}
          </h2>
          <p className="text-[13px] text-[color:var(--ink-muted)] mb-3">
            {t('employees.ess_tab_description')}
          </p>

          <div className="flex items-center gap-2.5 mb-4">
            <span className="text-[13px]">{t('employees.ess_status')}</span>
            <EssStatusBadge status={status} />
          </div>

          {ess.create_invite && (
            <div className="flex flex-wrap items-center gap-2.5">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handle_create()}
                disabled={create_button_disabled}
                data-testid="ess-send-invite"
              >
                <EssInviteActionLabel action={invite_action ?? 'invite'} />
              </Button>
              {status === 'linked' && ess.revoke_access && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    set_revoke_access_open(true);
                  }}
                  disabled={is_submitting}
                  data-testid="ess-revoke-access"
                >
                  {t('employees.ess_revoke_access')}
                </Button>
              )}
            </div>
          )}

          {!ess_allowed && (
            <p className="nv-help mt-3" data-testid="ess-contractor-note">
              {t('employees.contractor_no_ess')}
            </p>
          )}

          {status === 'no_email' && action_error === null && (
            <p className="nv-help mt-3" data-testid="ess-no-email-hint">
              {t('employees.ess_action_error_no_email')}
            </p>
          )}

          {action_error && (
            <p className="nv-help is-error mt-3" role="alert">
              {t(ESS_ACTION_ERROR_KEY[action_error])}
            </p>
          )}

          {emailed_to !== null && (
            <div
              className="mt-4 rounded-xl border border-[color:var(--line)] p-3"
              data-testid="ess-invite-emailed-panel"
            >
              <p className="text-[13px] font-semibold mb-1">
                {t('employees.ess_invite_emailed_title')}
              </p>
              <p className="text-[12.5px] text-[color:var(--ink-muted)] mb-2">
                {t('employees.ess_invite_emailed_confirmation', {
                  email: emailed_to,
                })}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  set_emailed_to(null);
                }}
                data-testid="ess-dismiss-emailed"
              >
                {t('common.close')}
              </Button>
            </div>
          )}

          {/*
            Inline reveal panel. The modal below is the unmissable moment of
            the mint, but it is trivially dismissed — and the raw token exists
            NOWHERE else: `createEssInvite` persists only
            `invite_token_hash` and `listEssInvites` never projects the token,
            so a dismissed dialog means the only way to get a usable link is
            to revoke and re-invite. This keeps the link on the page until the
            employer explicitly dismisses it.
          */}
          {revealed !== null && (
            <div
              className="mt-4 rounded-xl border border-[color:var(--line)] p-3"
              data-testid="ess-invite-link-panel"
            >
              <p className="text-[13px] font-semibold mb-1">
                {t('employees.ess_invite_sent_title')}
              </p>
              <p className="text-[12.5px] text-[color:var(--ink-muted)] mb-2">
                {t('employees.ess_invite_link_fallback_desc')}
              </p>
              <p
                className="nv-help is-error mb-2"
                role="alert"
                data-testid="ess-invite-email-failed"
              >
                {t('employees.ess_invite_email_failed')}
              </p>
              {/*
                `select-all` (user-select: all) makes a single tap select the
                WHOLE link, so press-and-hold → Copy works on a phone even
                when the button's clipboard path is unavailable — the manual
                route has to stay usable, because the raw token is not
                re-fetchable once this panel is dismissed.
              */}
              <div
                ref={link_element_ref}
                className="nv-input select-all"
                style={{ wordBreak: 'break-all' }}
                data-testid="ess-invite-link-value"
              >
                {revealed.invite_link}
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2.5">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void copy_link_to_clipboard()}
                  data-testid="ess-copy-link-inline"
                >
                  {copied
                    ? t('employees.ess_link_copied')
                    : t('employees.ess_copy_link')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={dismiss_revealed}
                  data-testid="ess-dismiss-link"
                >
                  {t('common.close')}
                </Button>
              </div>
              {copy_failed && (
                <p
                  className="nv-help is-error mt-2"
                  role="alert"
                  data-testid="ess-copy-link-failed"
                >
                  {t('employees.ess_copy_link_failed')}
                </p>
              )}
            </div>
          )}
        </GlassCard>

        <GlassCard className="rounded-2xl p-5">
          <h2 className="text-[14.5px] font-bold mb-3">
            {t('employees.ess_history_title')}
          </h2>
          {per_employee_invites.length === 0 ? (
            <p className="text-[13px] text-[color:var(--ink-muted)]">
              {t('employees.ess_history_empty')}
            </p>
          ) : (
            <table className="nv-table">
              <thead>
                <tr>
                  <th>{t('employees.ess_history_column_status')}</th>
                  <th>{t('employees.ess_history_column_created')}</th>
                  <th>{t('employees.ess_history_column_expires')}</th>
                  <th aria-label={t('employees.ess_history_column_actions')} />
                </tr>
              </thead>
              <tbody>
                {per_employee_invites.map((invite) => (
                  <InviteRow
                    key={invite.id}
                    invite={invite}
                    locale={locale}
                    now_iso={now_iso}
                    canRevoke={
                      ess.revoke_invite !== undefined &&
                      invite.status === 'pending' &&
                      !isEssInviteExpired(invite, now_iso)
                    }
                    onRevoke={() => {
                      set_pending_revoke_invite(invite.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </GlassCard>
      </div>

      {/*
        Dismissing the modal hands the link off to the inline panel above
        rather than destroying it: `revealed` is only dropped by
        `dismiss_revealed`, the single place the token leaves this state
        (adversarial-review Finding #2 for #23314).
      */}
      <EssInviteLinkDialog
        link={revealed?.invite_link ?? null}
        open={reveal_open}
        onOpenChange={set_reveal_open}
      />

      <ConfirmDialog
        open={pending_revoke_invite !== null}
        onOpenChange={(open) => {
          if (!open) set_pending_revoke_invite(null);
        }}
        title={t('employees.ess_revoke_invite_confirm_title')}
        description={t('employees.ess_revoke_invite_confirm_desc')}
        confirmLabel={t('employees.ess_revoke_confirm_button')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={is_submitting}
        onConfirm={() => {
          if (pending_revoke_invite) {
            void handle_revoke_invite(pending_revoke_invite);
          }
        }}
        testid="ess-revoke-invite-confirm"
      />

      <ConfirmDialog
        open={revoke_access_open}
        onOpenChange={set_revoke_access_open}
        title={t('employees.ess_revoke_access_confirm_title')}
        description={t('employees.ess_revoke_access_confirm_desc')}
        confirmLabel={t('employees.ess_revoke_confirm_button')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={is_submitting}
        onConfirm={() => void handle_revoke_access()}
        testid="ess-revoke-access-confirm"
      />
    </>
  );
}

function InviteRow({
  invite,
  locale,
  now_iso,
  canRevoke,
  onRevoke,
}: {
  invite: EssInvite;
  locale: string;
  now_iso: string;
  canRevoke: boolean;
  onRevoke: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  // A `pending` row past `expires_at` can no longer be accepted — show it as
  // expired rather than as a live invite.
  const row_status: EssInviteRowStatus = isEssInviteExpired(invite, now_iso)
    ? 'expired'
    : invite.status;
  return (
    <tr>
      <td>
        <EssInviteRowBadge status={row_status} />
      </td>
      <td>{format_date(invite.created_at, locale)}</td>
      <td>{format_date(invite.expires_at, locale)}</td>
      <td className="text-right">
        {canRevoke && (
          <Button
            variant="ghost-danger"
            size="sm"
            onClick={onRevoke}
            data-testid="ess-history-revoke-invite"
          >
            {t('employees.ess_revoke_invite')}
          </Button>
        )}
      </td>
    </tr>
  );
}
